import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClipboardCheck,
  FaFileMedical,
  FaHospitalUser,
  FaUserClock,
  FaClock,
  FaGraduationCap,
  FaAward,
} from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import PageLoader from "../../components/PageLoader";
import { supabase } from "../../supabaseClient";
import { signOutWithAudit } from "../../utils/audit";
import { getCompetencyStatus, isCompetencyRequiredForRole } from "../../utils/competencies";
import { formatProfileName } from "../../utils/profileName";

const QUICK_LINKS = [
  {
    label: "My timesheet",
    description: "Record worked time and review approval or payment status.",
    to: "/intranet/timesheet",
    icon: FaClock,
  },
  {
    label: "Patient lookup",
    description: "Find and open an existing eCAMIS patient record.",
    to: "/intranet/ecamis/lookup",
    icon: FaHospitalUser,
  },
  {
    label: "New appointment",
    description: "Create and assign a patient appointment.",
    to: "/intranet/ecamis/new-appointment",
    icon: FaCalendarAlt,
  },
  {
    label: "DATIX report",
    description: "Record an incident or safety concern.",
    to: "/intranet/datix",
    icon: FaFileMedical,
  },
  {
    label: "My schedule",
    description: "Review upcoming shifts and attendance status.",
    to: "/intranet/myschedule",
    icon: FaUserClock,
  },
  {
    label: "My workplace",
    description: "Availability, shift cover, policies, documents and appraisals.",
    to: "/intranet/my-workplace",
    icon: FaClipboardCheck,
  },
  {
    label: "My E-Learning",
    description: "Complete mandatory training and development learning.",
    to: "/intranet/e-learning",
    icon: FaGraduationCap,
  },
  {
    label: "My competencies",
    description: "Review certification, expiry dates and role requirements.",
    to: "/intranet/competencies",
    icon: FaAward,
  },
];

function formatDateTime(value) {
  if (!value) return "Date not set";
  return new Date(value).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getShiftDurationMinutes(shift) {
  const duration = Number(shift?.duration_minutes);
  return Number.isFinite(duration) && duration > 0 ? duration : 120;
}

function getShiftEndTime(shift) {
  const start = new Date(shift?.time).getTime();
  if (!Number.isFinite(start)) return null;
  return new Date(start + getShiftDurationMinutes(shift) * 60 * 1000);
}

export default function Portal({ session, profile: suppliedProfile }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(suppliedProfile || null);
  const [shifts, setShifts] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [learningAssignments, setLearningAssignments] = useState([]);
  const [learningProgress, setLearningProgress] = useState([]);
  const [competencyCatalogue, setCompetencyCatalogue] = useState([]);
  const [competencyRecords, setCompetencyRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [attendanceMessage, setAttendanceMessage] = useState("");
  const [clockNow, setClockNow] = useState(() => Date.now());

  useEffect(() => {
    setProfile(suppliedProfile || null);
  }, [suppliedProfile]);

  useEffect(() => {
    const interval = window.setInterval(() => setClockNow(Date.now()), 30000);
    return () => window.clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setLoadError("");

    try {
      let currentProfile = suppliedProfile || profile;

      if (!currentProfile && session?.user?.id) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();
        if (error) throw error;
        currentProfile = data || null;
        setProfile(currentProfile);
      }

      const [{ data: shiftData, error: shiftError }, { data: vacancyData, error: vacancyError }] =
        await Promise.all([
          supabase.from("shifts").select("*").order("time", { ascending: true }),
          supabase
            .from("dc_vacancies")
            .select("id, title, description, department, location, closing_date, status, is_active, created_at")
            .eq("status", "Open")
            .eq("is_active", true)
            .order("created_at", { ascending: false }),
        ]);

      if (shiftError) throw shiftError;
      if (vacancyError) throw vacancyError;

      setShifts(shiftData || []);
      setVacancies(vacancyData || []);

      if (session?.user?.id) {
        const [
          learningAssignmentResult,
          learningProgressResult,
          competencyCatalogueResult,
          competencyRecordResult,
        ] = await Promise.all([
          supabase
            .from("e_learning_assignments")
            .select(`
              id,
              due_at,
              revoked_at,
              module:e_learning_modules!e_learning_assignments_module_id_fkey(
                id, title, mandatory, category
              )
            `)
            .eq("user_id", session.user.id)
            .is("revoked_at", null),
          supabase
            .from("e_learning_progress")
            .select("assignment_id, status, completed_at, best_score")
            .eq("user_id", session.user.id),
          supabase
            .from("staff_competency_catalog")
            .select("id, title, required_job_roles, active")
            .eq("active", true),
          supabase
            .from("staff_competency_records")
            .select("id, competency_id, achieved_on, expires_on, verified")
            .eq("user_id", session.user.id),
        ]);

        if (learningAssignmentResult.error || learningProgressResult.error) {
          console.warn(
            "E-Learning summary could not be loaded:",
            learningAssignmentResult.error || learningProgressResult.error,
          );
          setLearningAssignments([]);
          setLearningProgress([]);
        } else {
          setLearningAssignments(learningAssignmentResult.data || []);
          setLearningProgress(learningProgressResult.data || []);
        }

        if (competencyCatalogueResult.error || competencyRecordResult.error) {
          console.warn(
            "Competency summary could not be loaded:",
            competencyCatalogueResult.error || competencyRecordResult.error,
          );
          setCompetencyCatalogue([]);
          setCompetencyRecords([]);
        } else {
          setCompetencyCatalogue(competencyCatalogueResult.data || []);
          setCompetencyRecords(competencyRecordResult.data || []);
        }
      }
    } catch (error) {
      console.error("Unable to load the staff dashboard:", error);
      setLoadError(
        "Some dashboard information could not be loaded. Try again in a moment.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [session?.user?.id]);

  const ongoingShifts = useMemo(
    () =>
      shifts.filter((shift) => {
        const start = new Date(shift.time).getTime();
        const end = getShiftEndTime(shift)?.getTime();
        return Number.isFinite(start) && Number.isFinite(end) && start <= clockNow && clockNow < end;
      }),
    [clockNow, shifts],
  );

  const upcomingShifts = useMemo(
    () =>
      shifts.filter((shift) => {
        const start = new Date(shift.time).getTime();
        return Number.isFinite(start) && start > clockNow;
      }),
    [clockNow, shifts],
  );

  const learningProgressMap = useMemo(
    () => Object.fromEntries(learningProgress.map((row) => [row.assignment_id, row])),
    [learningProgress],
  );

  const outstandingMandatoryLearning = useMemo(
    () => learningAssignments.filter(
      (row) => row.module?.mandatory && learningProgressMap[row.id]?.status !== "Completed",
    ),
    [learningAssignments, learningProgressMap],
  );

  const learningDueSoon = useMemo(
    () => learningAssignments.filter((row) => {
      if (learningProgressMap[row.id]?.status === "Completed" || !row.due_at) return false;
      return new Date(row.due_at).getTime() - clockNow <= 7 * 24 * 60 * 60 * 1000;
    }),
    [clockNow, learningAssignments, learningProgressMap],
  );

  const competencyRecordMap = useMemo(
    () => Object.fromEntries(competencyRecords.map((row) => [row.competency_id, row])),
    [competencyRecords],
  );

  const competencyAttention = useMemo(() => {
    const required = competencyCatalogue.filter((definition) =>
      isCompetencyRequiredForRole(definition, profile?.job_role),
    );
    return required.filter((definition) =>
      getCompetencyStatus(competencyRecordMap[definition.id] || null, { required: true }).attention,
    );
  }, [competencyCatalogue, competencyRecordMap, profile?.job_role]);

  const profileName = formatProfileName(profile);
  const isAuthorised = ["Staff", "Management"].includes(profile?.position);

  const getUserStatus = (attendees = []) => {
    const entry = attendees.find((attendee) => attendee.startsWith(profileName));
    return entry ? entry.split(" - ")[1] || "Unassigned" : "Unassigned";
  };

  const updateAttendance = async (shift, status) => {
    setAttendanceMessage("");

    const existingAttendees = Array.isArray(shift.attendees) ? shift.attendees : [];
    const filtered = existingAttendees.filter(
      (attendee) => !attendee.startsWith(profileName),
    );
    const updatedAttendees =
      status === "Unassigned"
        ? filtered
        : [...filtered, `${profileName} - ${status}`];

    const { error } = await supabase
      .from("shifts")
      .update({ attendees: updatedAttendees })
      .eq("id", shift.id);

    if (error) {
      setAttendanceMessage("Your attendance status could not be updated.");
      return;
    }

    setShifts((current) =>
      current.map((item) =>
        item.id === shift.id ? { ...item, attendees: updatedAttendees } : item,
      ),
    );
    setAttendanceMessage("Your attendance status has been updated.");
  };

  const handleLogout = async () => {
    localStorage.removeItem("pguh:lastRoute");
    localStorage.removeItem("pguh:lastExactRoute");
    await signOutWithAudit();
    navigate("/login", { replace: true });
  };

  if (loading) return <PageLoader label="Loading your staff dashboard" />;

  if (!isAuthorised) {
    return (
      <div className="pguh-page-shell">
        <main className="pguh-main">
          <div className="nhsuk-width-container pguh-narrow-container">
            <div className="nhsuk-error-summary" role="alert">
              <h1 className="nhsuk-error-summary__title">Staff access is required</h1>
              <div className="nhsuk-error-summary__body">
                <p>
                  Your account is active, but it is currently registered as
                  <strong> {profile?.position || "Visitor"}</strong>.
                </p>
                <p>
                  Ask a management team member to update your staff position if
                  you believe this is incorrect.
                </p>
                <button className="nhsuk-button" type="button" onClick={handleLogout}>
                  Log out
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />

      <main className="pguh-main">
        <div className="nhsuk-width-container">
          <section className="pguh-dashboard-hero">
            <div>
              <span className="pguh-eyebrow">Staff intranet</span>
              <h1 className="nhsuk-heading-xl">Welcome, {profileName}</h1>
              <p className="nhsuk-body-l">
                Access the clinical, staffing and management services you use most.
              </p>
            </div>
            <div className="pguh-dashboard-identity" aria-label="Account summary">
              <span>Signed in as</span>
              <strong>{profile?.position}</strong>
              {profile?.roblox_name && <small>@{profile.roblox_name}</small>}
            </div>
          </section>

          {loadError && (
            <div className="nhsuk-warning-callout" role="alert">
              <h2 className="nhsuk-warning-callout__label">Dashboard warning</h2>
              <p>{loadError}</p>
              <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={loadDashboard}>
                Try again
              </button>
            </div>
          )}

          <section aria-labelledby="quick-actions-heading">
            <div className="pguh-section-heading">
              <div>
                <span className="pguh-eyebrow">Quick actions</span>
                <h2 id="quick-actions-heading" className="nhsuk-heading-l">
                  What do you need to do?
                </h2>
              </div>
            </div>

            <div className="pguh-quick-grid">
              {QUICK_LINKS.map(({ label, description, to, icon: Icon }) => (
                <Link className="pguh-quick-card" to={to} key={to}>
                  <span className="pguh-quick-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span>
                    <strong>{label}</strong>
                    <small>{description}</small>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="pguh-dashboard-stats" aria-label="Dashboard summary">
            <div className="pguh-stat-card pguh-stat-card--ongoing">
              <span>Ongoing shifts</span>
              <strong>{ongoingShifts.length}</strong>
              <Link to="/intranet/calendar">Open calendar</Link>
            </div>
            <div className="pguh-stat-card">
              <span>Upcoming shifts</span>
              <strong>{upcomingShifts.length}</strong>
              <Link to="/intranet/calendar">Open calendar</Link>
            </div>
            <div className="pguh-stat-card">
              <span>Open vacancies</span>
              <strong>{vacancies.length}</strong>
              <Link to="/intranet/vacancies">View vacancies</Link>
            </div>
            <div className="pguh-stat-card">
              <span>Mandatory E-Learning</span>
              <strong>{outstandingMandatoryLearning.length}</strong>
              <Link to="/intranet/e-learning">{learningDueSoon.length ? `${learningDueSoon.length} due soon` : "Open learning"}</Link>
            </div>
            <div className="pguh-stat-card">
              <span>Competency attention</span>
              <strong>{competencyAttention.length}</strong>
              <Link to="/intranet/competencies">{competencyAttention.length ? "Review requirements" : "View competencies"}</Link>
            </div>
            <div className="pguh-stat-card pguh-stat-card--rating">
              <span>RCQC rating</span>
              <strong>Good</strong>
              <small>January 2026</small>
            </div>
          </section>

          <section className="pguh-panel pguh-ongoing-shifts-panel" aria-labelledby="ongoing-shifts-heading">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Live rota</span>
                <h2 id="ongoing-shifts-heading" className="nhsuk-heading-l">
                  Ongoing shifts
                </h2>
              </div>
              <Link to="/intranet/myschedule">View full schedule</Link>
            </div>

            {ongoingShifts.length === 0 ? (
              <div className="pguh-empty-state">
                <FaClock aria-hidden="true" />
                <h3>No shifts are currently ongoing</h3>
                <p>Active shifts will appear here automatically once their start time is reached.</p>
              </div>
            ) : (
              <div className="pguh-ongoing-shifts-grid">
                {ongoingShifts.map((shift) => {
                  const endTime = getShiftEndTime(shift);
                  return (
                    <article className="pguh-ongoing-shift-card" key={shift.id}>
                      <span className="pguh-ongoing-shift-card__status">Ongoing now</span>
                      <h3>{shift.title || "Hospital shift"}</h3>
                      <dl className="pguh-compact-details">
                        <div>
                          <dt>Started</dt>
                          <dd>{formatDateTime(shift.time)}</dd>
                        </div>
                        <div>
                          <dt>Ends</dt>
                          <dd>{formatDateTime(endTime)}</dd>
                        </div>
                        <div>
                          <dt>Location</dt>
                          <dd>{shift.location || "Not specified"}</dd>
                        </div>
                        <div>
                          <dt>Host</dt>
                          <dd>{shift.host || "Not assigned"}</dd>
                        </div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <div className="pguh-dashboard-columns">
            <section className="pguh-panel" aria-labelledby="shifts-heading">
              <div className="pguh-panel__header">
                <div>
                  <span className="pguh-eyebrow">Your rota</span>
                  <h2 id="shifts-heading" className="nhsuk-heading-l">
                    Upcoming shifts
                  </h2>
                </div>
                <Link to="/intranet/myschedule">View full schedule</Link>
              </div>

              {attendanceMessage && (
                <p className="pguh-inline-status" role="status">
                  {attendanceMessage}
                </p>
              )}

              {upcomingShifts.length === 0 ? (
                <div className="pguh-empty-state">
                  <FaCalendarAlt aria-hidden="true" />
                  <h3>No upcoming shifts</h3>
                  <p>New shifts will appear here after they are scheduled.</p>
                </div>
              ) : (
                <div className="pguh-list-stack">
                  {upcomingShifts.slice(0, 5).map((shift) => (
                    <article className="pguh-shift-row" key={shift.id}>
                      <div>
                        <h3>{shift.title || "Hospital shift"}</h3>
                        <dl className="pguh-compact-details">
                          <div>
                            <dt>When</dt>
                            <dd>{formatDateTime(shift.time)}</dd>
                          </div>
                          <div>
                            <dt>Location</dt>
                            <dd>{shift.location || "Not specified"}</dd>
                          </div>
                          <div>
                            <dt>Host</dt>
                            <dd>{shift.host || "Not assigned"}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="pguh-shift-row__action">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor={`attendance-${shift.id}`}>
                          Attendance
                        </label>
                        <select
                          id={`attendance-${shift.id}`}
                          className="nhsuk-select"
                          value={getUserStatus(shift.attendees || [])}
                          onChange={(event) => updateAttendance(shift, event.target.value)}
                        >
                          <option value="Unassigned">Not attending</option>
                          <option value="Attending">Attending</option>
                          <option value="On Standby">On standby</option>
                        </select>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <aside className="pguh-panel" aria-labelledby="vacancies-heading">
              <div className="pguh-panel__header">
                <div>
                  <span className="pguh-eyebrow">Development</span>
                  <h2 id="vacancies-heading" className="nhsuk-heading-l">
                    Internal vacancies
                  </h2>
                </div>
                <Link to="/intranet/vacancies">View all</Link>
              </div>

              {vacancies.length === 0 ? (
                <div className="pguh-empty-state">
                  <FaClipboardCheck aria-hidden="true" />
                  <h3>No open vacancies</h3>
                  <p>New internal opportunities will appear here.</p>
                </div>
              ) : (
                <div className="pguh-list-stack">
                  {vacancies.slice(0, 4).map((vacancy) => (
                    <article className="pguh-vacancy-row" key={vacancy.id}>
                      <h3>
                        <Link to={`/intranet/vacancy/${vacancy.id}`}>
                          {vacancy.title}
                        </Link>
                      </h3>
                      <p>{vacancy.description || vacancy.department || "Internal opportunity"}</p>
                      <small>
                        Posted {new Date(vacancy.created_at).toLocaleDateString("en-GB")}
                      </small>
                    </article>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
